import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import PdfSection, { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfTableComponent from '../PdfComponents/PdfTable';
import { voucherStatusesEnum } from '../../../api/vouchers/helpers';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';

import { VouchersPdfPropsType } from './voucherHelpers';

const AgentInvoicePdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            referenceNumber,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                agentInfo,
                columns,
                rows,
                summaryLine,
                rateTableColumns,
                rateTableRows,
                rateLine,
                bottomDetails,
                topLeftDetailsText,
                voucherCertification = 'Draft',
                linkedInvoiceNumber,
                aciRef
            }
        },
        number: voucherNumber,
        type: voucherType,
        status,
        createdAt
    } = props;

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {topLeftDetailsText ? <Text>{topLeftDetailsText}</Text> : null}
                                {agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : null}
                            </View>

                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                                issuedBy={userName}
                                iataLast
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                    </View>

                    <View style={[styles.section, styles.alignCenter]} wrap={false}>
                        {status === voucherStatusesEnum.CANCEL ? (
                            <Text style={[styles.paymentStatus, styles.marginTopMinus24BottomMinus14]}>
                                {'CANCELLED'}
                            </Text>
                        ) : (
                            <></>
                        )}
                        <PdfMainDetails
                            topDetails={`Agent Commission Invoice No ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel
                        />
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent columns={columns} rows={rows} summaryLine={summaryLine} />
                    </View>

                    {/* Rate, Reference, Linked Invoice — clean label:value style */}
                    {(rateLine || referenceNumber || aciRef || linkedInvoiceNumber) ? (
                        <View style={[styles.section, { marginTop: 8, marginBottom: 12 }]}>
                            {rateLine ? (
                                <View style={{ flexDirection: 'row', paddingVertical: 1 }}>
                                    <Text style={{ color: '#777', fontSize: BASE_FONT_SIZE, width: 110 }}>Rate:</Text>
                                    <Text style={{ fontSize: BASE_FONT_SIZE, fontWeight: 'bold' }}>{rateLine.replace(/.*Rate:\s*/, '')}</Text>
                                </View>
                            ) : <></>}
                            {referenceNumber ? (
                                <View style={{ flexDirection: 'row', paddingVertical: 1 }}>
                                    <Text style={{ color: '#777', fontSize: BASE_FONT_SIZE, width: 110 }}>Reference No.:</Text>
                                    <Text style={{ fontSize: BASE_FONT_SIZE, fontWeight: 'bold' }}>{referenceNumber}</Text>
                                </View>
                            ) : <></>}
                            {aciRef ? (
                                <View style={{ flexDirection: 'row', paddingVertical: 1 }}>
                                    <Text style={{ color: '#777', fontSize: BASE_FONT_SIZE, width: 110 }}>ACI Reference:</Text>
                                    <Text style={{ fontSize: BASE_FONT_SIZE, fontWeight: 'bold' }}>{aciRef}</Text>
                                </View>
                            ) : <></>}
                            {linkedInvoiceNumber ? (
                                <View style={{ flexDirection: 'row', paddingVertical: 1 }}>
                                    <Text style={{ color: '#777', fontSize: BASE_FONT_SIZE, width: 110 }}>Linked to invoice:</Text>
                                    <Text style={{ fontSize: BASE_FONT_SIZE, fontWeight: 'bold' }}>{linkedInvoiceNumber}</Text>
                                </View>
                            ) : <></>}
                        </View>
                    ) : <></>}

                    <PdfRemarksSection body={remark} />
                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(AgentInvoicePdf);
